import { useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { TetrazolioListParams } from '../../../../services/sementes/tetrazolio';

export interface FilterValues {
  search: string;
  dataInicio: Date | undefined;
  dataFim: Date | undefined;
  statusPendente: boolean;
  statusIniciado: boolean;
  statusRealizado: boolean;
}

function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

const validationSchema = Yup.object({
  search: Yup.string(),
  dataInicio: Yup.date().required('Data inicial é obrigatória'),
  dataFim: Yup.date().required('Data final é obrigatória')
    .when('dataInicio', (dataInicio: any, schema: any) => (dataInicio ? schema.min(dataInicio, 'Data fim deve ser maior que data início') : schema)),
  statusPendente: Yup.boolean(),
  statusIniciado: Yup.boolean(),
  statusRealizado: Yup.boolean(),
}).test({
  name: 'at-least-one-status',
  message: 'Selecione ao menos um status',
  test: (values) => !!(values.statusPendente || values.statusIniciado || values.statusRealizado),
});

export interface TetrazolioFiltersHookData {
  formik: any;
  clearFilters: () => void;
  getFiltersFromURL: () => TetrazolioListParams;
  changePage: (page: number) => void;
}

export default function useTetrazolioFilters(urlPrefix = 'tz'): TetrazolioFiltersHookData {
  const history = useHistory();
  const location = useLocation();

  const getInitialValues = useCallback((): FilterValues => {
    const searchParams = new URLSearchParams(location.search);
    const status = searchParams.get(`${urlPrefix}_status`) || '';
    const statusArray = status ? status.split(',') : [];

    const hoje = new Date();
    const setediasAtras = new Date();
    setediasAtras.setDate(hoje.getDate() - 7);

    const hasAnyFilter = searchParams.has(`${urlPrefix}_search`)
      || searchParams.has(`${urlPrefix}_status`)
      || searchParams.has(`${urlPrefix}_dataInicio`)
      || searchParams.has(`${urlPrefix}_dataFim`);

    let dataInicio: Date | undefined;
    if (searchParams.get(`${urlPrefix}_dataInicio`)) {
      dataInicio = parseLocalDate(searchParams.get(`${urlPrefix}_dataInicio`)!);
    } else if (!hasAnyFilter) {
      dataInicio = setediasAtras;
    }

    let dataFim: Date | undefined;
    if (searchParams.get(`${urlPrefix}_dataFim`)) {
      dataFim = parseLocalDate(searchParams.get(`${urlPrefix}_dataFim`)!);
    } else if (!hasAnyFilter) {
      dataFim = hoje;
    }

    return {
      search: searchParams.get(`${urlPrefix}_search`) || '',
      dataInicio,
      dataFim,
      statusPendente: statusArray.length > 0 ? statusArray.includes('Pendente') : !hasAnyFilter,
      statusIniciado: statusArray.length > 0 ? statusArray.includes('Iniciado') : !hasAnyFilter,
      statusRealizado: statusArray.includes('Realizado'),
    };
  }, [location.search, urlPrefix]);

  const updateURL = useCallback((filters: Partial<TetrazolioListParams>) => {
    const searchParams = new URLSearchParams(location.search);

    Object.entries(filters).forEach(([key, value]) => {
      const prefixedKey = `${urlPrefix}_${key}`;
      if (value !== null && value !== undefined && value !== '') {
        searchParams.set(prefixedKey, String(value));
      } else {
        searchParams.delete(prefixedKey);
      }
    });

    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }, [history, location.pathname, location.search, urlPrefix]);

  const formik = useFormik<FilterValues>({
    initialValues: getInitialValues(),
    enableReinitialize: false,
    validationSchema,
    onSubmit: (values) => {
      // Valida se ao menos um status foi selecionado
      const selectedStatuses = [];
      if (values.statusPendente) selectedStatuses.push('Pendente');
      if (values.statusIniciado) selectedStatuses.push('Iniciado');
      if (values.statusRealizado) selectedStatuses.push('Realizado');

      if (selectedStatuses.length === 0) {
        formik.setFieldError('at-least-one-status', 'Selecione ao menos um status');
        return;
      }

      const filters: any = {
        page: 1,
        search: values.search || '',
      };

      filters.status = selectedStatuses.join(',');

      if (values.dataInicio) {
        [filters.dataInicio] = values.dataInicio.toISOString().split('T');
      }
      if (values.dataFim) {
        [filters.dataFim] = values.dataFim.toISOString().split('T');
      }

      updateURL(filters);
    },
  });

  // Sincroniza os valores do formulário com a URL quando ela muda
  useEffect(() => {
    const newValues = getInitialValues();
    formik.setValues(newValues, false);
  }, [location.search]); // eslint-disable-line

  const getFiltersFromURL = useCallback((): TetrazolioListParams => {
    const searchParams = new URLSearchParams(location.search);

    // Verifica se há algum filtro na URL
    const hasAnyFilter = searchParams.has(`${urlPrefix}_search`)
      || searchParams.has(`${urlPrefix}_status`)
      || searchParams.has(`${urlPrefix}_dataInicio`)
      || searchParams.has(`${urlPrefix}_dataFim`);

    // Se não houver filtros na URL, usa os padrões
    let dataInicio = searchParams.get(`${urlPrefix}_dataInicio`) || '';
    let dataFim = searchParams.get(`${urlPrefix}_dataFim`) || '';
    let status = searchParams.get(`${urlPrefix}_status`) || '';

    if (!hasAnyFilter) {
      const hoje = new Date();
      const setediasAtras = new Date();
      setediasAtras.setDate(hoje.getDate() - 7);
      [dataInicio] = setediasAtras.toISOString().split('T');
      [dataFim] = hoje.toISOString().split('T');
      status = 'Pendente,Iniciado';
    }

    const params: TetrazolioListParams = {
      produtorId: '',
      page: Number(searchParams.get(`${urlPrefix}_page`)) || 1,
      perPage: Number(searchParams.get(`${urlPrefix}_perPage`)) || 10,
      search: searchParams.get(`${urlPrefix}_search`) || '',
      status,
      orderBy: searchParams.get(`${urlPrefix}_orderBy`) || 'data_entrada',
      orderDirection: (searchParams.get(`${urlPrefix}_orderDirection`) as 'asc' | 'desc') || 'desc',
      dataInicio,
      dataFim,
    };

    Object.keys(params).forEach((key) => {
      if (params[key as keyof TetrazolioListParams] === '') {
        delete params[key as keyof TetrazolioListParams];
      }
    });

    return params;
  }, [location.search, urlPrefix]);

  const changePage = useCallback((page: number) => {
    updateURL({ page });
  }, [updateURL]);

  const clearFilters = useCallback(() => {
    formik.resetForm();
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete(`${urlPrefix}_search`);
    searchParams.delete(`${urlPrefix}_status`);
    searchParams.delete(`${urlPrefix}_dataInicio`);
    searchParams.delete(`${urlPrefix}_dataFim`);
    searchParams.delete(`${urlPrefix}_orderBy`);
    searchParams.delete(`${urlPrefix}_orderDirection`);
    searchParams.delete(`${urlPrefix}_page`);
    searchParams.delete(`${urlPrefix}_perPage`);
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }, [history, location.pathname, formik, urlPrefix]);

  // Aplica filtros padrão na URL após montar (para sincronizar UI)
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      const searchParams = new URLSearchParams(location.search);
      const hasAnyFilter = searchParams.has(`${urlPrefix}_search`)
        || searchParams.has(`${urlPrefix}_status`)
        || searchParams.has(`${urlPrefix}_dataInicio`)
        || searchParams.has(`${urlPrefix}_dataFim`);

      if (!hasAnyFilter) {
        // Aplica filtros padrão na URL de forma assíncrona
        setTimeout(() => {
          const hoje = new Date();
          const setediasAtras = new Date();
          setediasAtras.setDate(hoje.getDate() - 7);

          updateURL({
            dataInicio: setediasAtras.toISOString().split('T')[0],
            dataFim: hoje.toISOString().split('T')[0],
            status: 'Pendente,Iniciado',
          });
        }, 0);
      }
    }
  }, [urlPrefix, updateURL]);

  return {
    formik,
    clearFilters,
    getFiltersFromURL,
    changePage,
  };
}
